import { motion } from "framer-motion";
import { Trophy, Flame, Sparkles, Award, Users, TrendingUp, ShieldCheck, Layers } from "lucide-react";

function Section({ title, icon: Icon, bg = "#FBBF24", children, testid }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="nb-card p-5 sm:p-6 mb-4"
      style={{ background: bg }}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-3">
        <Icon size={20} strokeWidth={2.5} />
        <h2 className="font-display font-black text-xl">{title}</h2>
      </div>
      {children}
    </motion.section>
  );
}

function Row({ label, points, extra, cap }) {
  return (
    <li className="flex items-center justify-between gap-2 border-b border-dashed border-neutral-400/40 py-2 last:border-0">
      <div className="min-w-0">
        <div className="font-semibold text-sm sm:text-base">{label}</div>
        {extra && <div className="text-[11px] text-neutral-700 mt-0.5">{extra}</div>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {cap && <span className="chip bg-white text-[10px]">{cap}</span>}
        <span className="chip bg-white font-black">{typeof points === "number" ? `+${points}` : points}</span>
      </div>
    </li>
  );
}

export default function Bonuses() {
  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-xl bg-ink text-gold flex items-center justify-center">
          <Sparkles size={26} strokeWidth={2.5} />
        </div>
        <div>
          <h1 className="font-display font-black text-2xl sm:text-3xl">Bonus Guide · v4</h1>
          <p className="text-sm text-neutral-600">30-day challenge · Jul 24 → Aug 24 · 3× 7-day weeks + 11-day Finals</p>
        </div>
      </div>

      <div className="nb-card p-5 sm:p-6 mb-4" style={{ background: "#F0FDF4" }}>
        <div className="flex items-center gap-2 mb-2"><TrendingUp size={18} /><h2 className="font-display font-black text-lg">How the score works</h2></div>
        <div className="text-sm space-y-1.5">
          <p>Every log adds to your running tally. Nothing subtracts.</p>
          <p>Week score = <code className="chip bg-white">habit points earned + bonus points earned</code></p>
          <p>Season score = <code className="chip bg-white">sum of every week's score + carryover</code></p>
          <p className="text-neutral-600 text-[12px] pt-1">The tally always grows through the challenge — bigger number wins on Aug 24.</p>
        </div>
      </div>

      <Section title="Bonuses" icon={Award} bg="#FEF3C7" testid="section-bonuses">
        <ul>
          <Row label="Perfect Day" points={8} extra="Day earned = 100% of what was available."
               cap="cap 2/wk · 3/wk in W4" />
          <Row label="Perfect Week" points={20} extra="Every day of the week at 100%. Mutually exclusive with Comeback."
               cap="cap 1/wk" />
          <Row label="No Zero" points={15} extra="No habit at ZERO or unlogged the whole week. Partials count."
               cap="cap 1/wk" />
          <Row label="Comeback" points={12} extra="A sub-50% day followed by a 100% day. Credited to the week of the 100% day."
               cap="cap 1/wk" />
          <Row label="Shared Win" points="+8 each" extra="BOTH players hit 100% on the same date."
               cap="cap 3/wk · 4/wk in W4" />
        </ul>
      </Section>

      <Section title="Group Streaks" icon={Layers} bg="#EDE9FE" testid="section-groups">
        <p className="text-sm mb-3">
          A group HOLDS for a week if EVERY habit in it was logged <b>FULL</b> on every scheduled day.
          Partial does not hold. Shadow habits count. Unscheduled days are skipped. Unlogged = zero.
          A break resets that group's counter to 0. Other groups are unaffected. No points are ever subtracted.
        </p>
        <table className="w-full text-sm border-2 border-ink rounded-xl overflow-hidden">
          <thead className="bg-ink text-gold">
            <tr><th className="p-2 text-left">Consecutive weeks held</th><th className="p-2 text-right">Bonus that week</th></tr>
          </thead>
          <tbody className="bg-white">
            {[["1st", "+4"], ["2nd", "+7"], ["3rd", "+10"], ["4th (Finals)", "+12"]].map(([w, p]) => (
              <tr key={w} className="border-t border-neutral-200"><td className="p-2">{w}</td><td className="p-2 text-right font-mono font-black">{p}</td></tr>
            ))}
          </tbody>
        </table>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
          <div className="chip bg-blue-100">SLF · Self Care</div>
          <div className="chip bg-red-100">IRN · Iron</div>
          <div className="chip bg-yellow-100">DPW · Deep Work</div>
          <div className="chip bg-indigo-100">STL · Stillness</div>
        </div>
        <p className="text-[11px] text-neutral-600 mt-2">Season max from streaks: 4 + 7 + 10 + 12 = <b>+33</b> per group × 4 groups = <b>+132</b></p>
      </Section>

      <Section title="Weekly Bonus Ceiling" icon={ShieldCheck} bg="#FEF2F2" testid="section-ceilings">
        <p className="text-sm mb-3">Every bonus point earned goes straight into your running tally — no cap, no divisor. Below is the max bonus points possible each week.</p>
        <table className="w-full text-sm border-2 border-ink rounded-xl overflow-hidden">
          <thead className="bg-ink text-gold">
            <tr>
              <th className="p-2 text-left">Source</th>
              <th className="p-2 text-right">W1</th>
              <th className="p-2 text-right">W2</th>
              <th className="p-2 text-right">W3</th>
              <th className="p-2 text-right">W4</th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {[
              ["Streaks", 16, 28, 40, 48],
              ["Perfect Day", 16, 16, 16, 24],
              ["Perfect Week", 20, 20, 20, 20],
              ["No Zero", 15, 15, 15, 15],
              ["Shared Win", 24, 24, 24, 32],
            ].map((r) => (
              <tr key={r[0]} className="border-t border-neutral-200">
                <td className="p-2">{r[0]}</td>
                {r.slice(1).map((v, i) => <td key={i} className="p-2 text-right font-mono">+{v}</td>)}
              </tr>
            ))}
            <tr className="border-t-2 border-ink bg-neutral-100 font-bold">
              <td className="p-2">Max bonus / week</td>
              <td className="p-2 text-right font-mono">+91</td>
              <td className="p-2 text-right font-mono">+103</td>
              <td className="p-2 text-right font-mono">+115</td>
              <td className="p-2 text-right font-mono">+139</td>
            </tr>
          </tbody>
        </table>
        <p className="text-[11px] text-neutral-600 mt-2">Season bonus ceiling: <b>+448</b> raw bonus points on top of habit points.</p>
      </Section>

      <Section title="Tiebreakers" icon={Trophy} bg="#FEF3C7" testid="section-tiebreak">
        <ol className="list-decimal list-inside text-sm space-y-1">
          <li>Higher <b>habit-completion %</b> for the season (bonuses excluded)</li>
          <li>More <b>Perfect Days</b></li>
          <li>Draw</li>
        </ol>
      </Section>
    </div>
  );
}
